import * as React from 'react';
import clsx from 'clsx';
import { useEchoStore } from '@/context/EchoContext';

import { SidebarToggle } from './sidebar/components/toggle/SidebarToggle';
import { SidebarHeader } from './sidebar/components/header/SidebarHeader';
import { SidebarNav } from './sidebar/components/nav/SidebarNav';
import { SidebarFooter } from './sidebar/components/footer/SidebarFooter';
import { SidebarUpgrade } from './sidebar/components/upgrade/SidebarUpgrade';

interface SideBarProps {
  onLogout: () => void;
  onSupport: () => void;
}

/**
 * Desktop sidebar container.
 *
 * Orchestrates the sidebar layout, state, and sub-components, and
 * applies a dynamic CSS `zoom` scale (clamped to [0.6, 1]) so the
 * full sidebar — nav, upgrade card, and footer — stays visible when
 * the viewport is shorter than the sidebar's natural content height
 * (high browser zoom or Windows display scaling > 100%).
 *
 * Scale recomputes on collapse/expand, content-driven height changes
 * (echo count, fan count), and window resize.
 */
export const SideBar: React.FC<SideBarProps> = ({ onLogout, onSupport }) => {
  const [isCollapsed, setIsCollapsed] = React.useState(false);
  const { myEchoes } = useEchoStore();

  const echoCount = myEchoes.length;

  const asideRef = React.useRef<HTMLElement>(null);
  const innerRef = React.useRef<HTMLDivElement>(null);

  const totalFans = React.useMemo(
    () => myEchoes.reduce((sum, echo) => sum + (echo.total_fans || 0), 0),
    [myEchoes]
  );

  const handleLogout = () => {
    onLogout();
  };

  const toggleSidebar = () => {
    setIsCollapsed(!isCollapsed);
  };

  React.useLayoutEffect(() => {
    const aside = asideRef.current;
    const inner = innerRef.current;
    if (!aside || !inner) return;

    const updateScale = () => {
      aside.style.zoom = '1';
      const naturalHeight = inner.scrollHeight;
      const viewportHeight = window.innerHeight;
      if (naturalHeight === 0) return;
      const scale = Math.max(0.6, Math.min(1, viewportHeight / naturalHeight));
      aside.style.zoom = String(scale);
    };

    updateScale();
    window.addEventListener('resize', updateScale);
    return () => window.removeEventListener('resize', updateScale);
  }, [isCollapsed, echoCount, totalFans]);

  return (
    <aside
      ref={asideRef}
      className={clsx(
        'hidden xl:flex',
        'relative z-20 h-full shrink-0 transition-all duration-300 ease-in-out',
        isCollapsed ? 'w-sidebar-collapsed' : 'w-sidebar-open'
      )}
    >
      <SidebarToggle isCollapsed={isCollapsed} onToggle={toggleSidebar} />

      <div
        ref={innerRef}
        className={clsx(
          'flex size-full flex-col',
          'bg-white',
          'overflow-hidden rounded-r-sidebar border-r-2 border-gray-400'
        )}
      >
        <SidebarHeader isCollapsed={isCollapsed} echoCount={echoCount} fanCount={totalFans} />

        <div
          className={clsx('flex min-h-0 flex-1 flex-col', 'bg-white', 'rounded-br-sidebar', 'pt-4')}
        >
          <div className='shrink-0'>
            <SidebarNav isCollapsed={isCollapsed} />
          </div>

          <div className='min-h-[20px] tall:flex-1' />

          <div className='shrink-0'>
            <SidebarUpgrade isCollapsed={isCollapsed} />
          </div>

          <div className='shrink-0'>
            <SidebarFooter
              isCollapsed={isCollapsed}
              onLogout={handleLogout}
              onSupport={onSupport}
            />
          </div>
        </div>
      </div>
    </aside>
  );
};

SideBar.displayName = 'SideBar';

export default SideBar;
